import { useRef } from "react";

interface UseHasUnresolvedProjectSwitchParams {
  /** 현재 선택된 프로젝트 id. 미선택이면 `undefined`. */
  selectedProjectId: number | undefined;
  /** 히스토리 쿼리가 표시할 데이터를 갖고 있는지 여부(`data !== undefined`). */
  hasHistoryData: boolean;
  /** react-query `keepPreviousData` 로 내려온 직전 쿼리의 데이터인지 여부. */
  isPreviousData: boolean;
  /** 히스토리 조회가 오류로 확정됐는지 여부. */
  isHistoryError: boolean;
}

/**
 * 현재 선택이 **기준 프로젝트와 다른 채로 아직 해소되지 않았는지** 판정하는 훅.
 *
 * "선택된 프로젝트의 데이터가 미도착인가"와는 다르다 — 기준 프로젝트로 잡힌 선택은 그
 * 데이터가 아직 도착하지 않았어도 `false` 다(아래 첫 선택 예외). 이 훅이 판정하는 것은
 * 어디까지나 **기준 대비 전환이 남아 있는가**이며, 최초 로딩 구간의 로딩 표시는 호출자가
 * react-query 의 `isLoading` 으로 따로 처리한다.
 *
 * keepPreviousData 는 필터 전환뿐 아니라 프로젝트 전환에서도 직전 데이터를 유지한다.
 * 필터 전환은 같은 프로젝트라 그대로 보여도 되지만, 프로젝트 전환 중 남은 직전 프로젝트의
 * 이벤트는 감사 화면에서 잘못된 귀속이 되므로 두 구간을 구분해야 한다.
 *
 * 기준점(switchBaselineProjectIdRef)은 "전환 판정의 기준이 되는 프로젝트"다.
 * - 갱신 조건은 **이 프로젝트의 데이터가 실제로 도착한 순간**(자기 data 가 있고 isPreviousData
 *   가 아님)뿐이다. isPreviousData 만으로 갱신하면, 직전 쿼리가 아직 한 번도 성공하지 않아
 *   isPreviousData 가 false 인 구간(첫 요청 in-flight)에서 도착하지도 않은 데이터를 도착으로
 *   기록해 그 상태로 프로젝트를 바꿔도 전환으로 잡히지 않는다.
 * - 단, 첫 선택은 "전환"이 아니므로 도착 전에도 기준점으로 잡아 둔다(이후의 선택 변경만 전환).
 *
 * 오류로 확정된 구간은 현재 선택에 대한 결과가 나온 것이므로 전환이 해소된 것으로 본다 —
 * 호출자가 이 값을 로딩으로 취급하는 이상, 포함하면 오류·권한없음 화면이 스피너에 영구히
 * 가려진다(기준점은 오류 시 갱신되지 않으므로).
 */
export const useHasUnresolvedProjectSwitch = ({
  selectedProjectId,
  hasHistoryData,
  isPreviousData,
  isHistoryError,
}: UseHasUnresolvedProjectSwitchParams): boolean => {
  const switchBaselineProjectIdRef = useRef<number | undefined>(undefined);
  const hasOwnHistoryData = hasHistoryData && !isPreviousData;
  if (hasOwnHistoryData || switchBaselineProjectIdRef.current === undefined) {
    switchBaselineProjectIdRef.current = selectedProjectId;
  }

  return (
    !isHistoryError && switchBaselineProjectIdRef.current !== selectedProjectId
  );
};
